// src/components/admin/pages/PrivacyPage.js
import React from 'react';
import { 
  Shield, Eye, Lock, Cookie, Mail, UserCheck, Database, 
  CheckCircle2, AlertCircle, Globe, Server, FileText, Users
} from 'lucide-react';

export default function PrivacyPage() {
  const lastUpdate = "10 de septiembre de 2026";

  return (
    <div className="w-full">
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 md:p-10 lg:p-12">
        {/* Header Sobrio */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-100 rounded-2xl mb-4">
            <Shield className="w-8 h-8 text-slate-700" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3 tracking-tight">
            Política de Privacidad
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Tu privacidad es nuestra prioridad. Te explicamos todo con claridad.
          </p>
          <p className="text-sm text-slate-500 mt-4 flex items-center justify-center gap-2">
            <Eye className="w-4 h-4" />
            Última actualización: {lastUpdate}
          </p>
        </div>

        {/* Promesa */}
        <div className="bg-blue-50/50 border border-blue-100 rounded-xl p-6 md:p-8 mb-12">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-white border border-blue-100 rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm">
              <Shield className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900 mb-2">Nuestra promesa</h2>
              <p className="text-slate-600 leading-relaxed">
                En Inkless, tratamos tus datos personales con el máximo cuidado. No los vendemos, 
                no los compartimos con fines de marketing, y hacemos todo lo posible por protegerlos. 
                Esta política te explica exactamente qué hacemos con tu información, en lenguaje claro 
                y sin sorpresas.
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-12">
          {/* SECCIÓN 1 */}
          <section className="border-b border-slate-200 pb-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
                <Database className="w-4 h-4 text-blue-600" />
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-slate-900">1. ¿Qué información recopilamos?</h2>
            </div>
            
            <p className="text-slate-600 mb-6 pl-11">
              Solo recopilamos lo que necesitamos para que el servicio funcione. Nada más.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pl-11">
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-5">
                <div className="w-10 h-10 bg-white border border-slate-200 rounded-lg flex items-center justify-center mb-3">
                  <Users className="w-5 h-5 text-slate-600" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-3">Lo que tú nos das:</h3>
                <ul className="space-y-3 text-sm text-slate-600">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span><strong>Para tu cuenta:</strong> Nombre, email y contraseña (encriptada, nunca la vemos en texto plano).</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span><strong>Para firmar:</strong> Los archivos PDF que subes y los nombres de tus clientes.</span>
                  </li>
                </ul>
              </div>

              <div className="bg-slate-50 border border-slate-200 rounded-lg p-5">
                <div className="w-10 h-10 bg-white border border-slate-200 rounded-lg flex items-center justify-center mb-3">
                  <Server className="w-5 h-5 text-slate-600" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-3">Recopilación automática:</h3>
                <ul className="space-y-3 text-sm text-slate-600">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span><strong>Datos técnicos:</strong> Tipo de navegador, sistema operativo, dirección IP (para seguridad).</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span><strong>Estadísticas de uso:</strong> Métricas anónimas sobre cómo se utiliza la plataforma para optimizar funcionalidades.</span>
                  </li>
                </ul>
              </div>

              <div className="bg-slate-50 border border-slate-200 rounded-lg p-5">
                <div className="w-10 h-10 bg-white border border-slate-200 rounded-lg flex items-center justify-center mb-3">
                  <Lock className="w-5 h-5 text-slate-600" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-3">Datos de seguridad:</h3>
                <ul className="space-y-3 text-sm text-slate-600">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span><strong>Hashes criptográficos:</strong> "Huellas digitales" de tus documentos para verificar integridad.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span><strong>Registros de auditoría:</strong> Cuándo y desde dónde se firmó cada documento.</span>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* SECCIÓN 2 */}
          <section className="border-b border-slate-200 pb-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
                <Eye className="w-4 h-4 text-blue-600" />
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-slate-900">2. ¿Para qué usamos tu información?</h2>
            </div>
            
            <p className="text-slate-600 mb-6 pl-11">
              Solo para cosas que te beneficien o que sean necesarias para el servicio:
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pl-11">
              {[
                { icon: Server, title: 'Hacer que todo funcione', desc: 'Crear tu cuenta, procesar documentos, generar enlaces.' },
                { icon: Mail, title: 'Mantenerte informado', desc: 'Notificarte cuando un documento sea firmado o esté por expirar.' },
                { icon: Shield, title: 'Protegerte', desc: 'Detectar fraudes y prevenir accesos no autorizados.' },
                { icon: FileText, title: 'Cumplir la ley', desc: 'Responder a solicitudes legales cuando sea estrictamente necesario.' }
              ].map((item, idx) => (
                <div key={idx} className="bg-slate-50 border border-slate-200 rounded-lg p-5">
                  <div className="w-10 h-10 bg-white border border-slate-200 rounded-lg flex items-center justify-center mb-3">
                    <item.icon className="w-5 h-5 text-slate-600" />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-slate-600">{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* SECCIÓN 3 - COOKIES */}
          <section className="border-b border-slate-200 pb-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
                <Cookie className="w-4 h-4 text-blue-600" />
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-slate-900">3. Cookies y publicidad</h2>
            </div>
            
            <p className="text-slate-600 mb-6 pl-11">
              Usamos cookies para que la plataforma funcione mejor y para mostrar anuncios relevantes 
              (que nos ayudan a mantener el servicio accesible).
            </p>

            <div className="bg-slate-50 border border-slate-200 rounded-xl p-6 md:p-8 mb-6 pl-11">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-10 h-10 bg-white border border-slate-200 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Cookie className="w-5 h-5 text-slate-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">Sobre Google AdSense</h3>
                  <p className="text-slate-600 leading-relaxed">
                    Usamos <strong>Google AdSense</strong> para mostrar anuncios en nuestra plataforma. 
                    Google utiliza cookies para mostrarte anuncios que puedan interesarte basándose en tu 
                    historial de navegación.
                  </p>
                </div>
              </div>
              <div className="bg-white rounded-lg p-5 mt-4 border border-slate-200">
                <p className="font-semibold text-slate-900 mb-3">¿Qué puedes hacer?</p>
                <ul className="space-y-3 text-sm text-slate-600">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span>
                      Optar por no recibir anuncios personalizados: 
                      <a href="https://www.aboutads.info/choices" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline ml-1">aboutads.info/choices</a>
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span>
                      Si estás en Europa: 
                      <a href="https://www.youronlinechoices.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline ml-1">youronlinechoices.com</a>
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* SECCIÓN 4 */}
          <section className="border-b border-slate-200 pb-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
                <UserCheck className="w-4 h-4 text-blue-600" />
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-slate-900">4. ¿Compartimos tus datos?</h2>
            </div>
            
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-6 md:p-8 mb-6 pl-11">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white border border-slate-200 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Shield className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-xl font-bold text-slate-900 mb-1">No. No vendemos tus datos. Nunca.</p>
                  <p className="text-slate-600">Tus datos permanecen bajo nuestro control. Solo utilizamos servicios técnicos certificados para operar la plataforma, y cumplimos con las obligaciones legales cuando corresponde.</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pl-11">
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-5">
                <div className="w-10 h-10 bg-white border border-slate-200 rounded-lg flex items-center justify-center mb-3">
                  <Server className="w-5 h-5 text-slate-600" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">Proveedores técnicos:</h3>
                <p className="text-sm text-slate-600 mb-3">
                  Empresas que nos ayudan a operar la plataforma. Todos tienen acuerdos de confidencialidad estrictos.
                </p>
                <div className="flex flex-wrap gap-2">
                  {['AWS', 'SendGrid', 'Google Analytics'].map((provider, idx) => (
                    <span key={idx} className="px-3 py-1 bg-white border border-slate-200 rounded-md text-xs font-medium text-slate-600">
                      {provider}
                    </span>
                  ))}
                </div>
              </div>

              <div className="bg-slate-50 border border-slate-200 rounded-lg p-5">
                <div className="w-10 h-10 bg-white border border-slate-200 rounded-lg flex items-center justify-center mb-3">
                  <FileText className="w-5 h-5 text-slate-600" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">Cuando la ley lo exige:</h3>
                <p className="text-sm text-slate-600">
                  Si una autoridad competente nos lo solicita legalmente (orden judicial, investigación de fraude, etc.).
                </p>
              </div>

              <div className="bg-slate-50 border border-slate-200 rounded-lg p-5">
                <div className="w-10 h-10 bg-white border border-slate-200 rounded-lg flex items-center justify-center mb-3">
                  <Globe className="w-5 h-5 text-slate-600" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">Transferencia de negocio:</h3>
                <p className="text-sm text-slate-600">
                  En caso de fusión o venta, te avisaríamos con tiempo y tus datos seguirían protegidos bajo esta misma política.
                </p>
              </div>
            </div>
          </section>

          {/* SECCIÓN 5 */}
          <section className="border-b border-slate-200 pb-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
                <Lock className="w-4 h-4 text-blue-600" />
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-slate-900">5. ¿Cómo protegemos tus datos?</h2>
            </div>
            
            <p className="text-slate-600 mb-6 pl-11">
              Con la misma seguridad que usan las instituciones financieras:
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pl-11">
              {[
                { icon: Lock, title: 'Encriptación total', desc: 'TLS 1.3 en tránsito, AES-256 en reposo.' },
                { icon: Shield, title: 'Autenticación robusta', desc: 'Tokens JWT y autenticación de dos factores (2FA).' },
                { icon: Server, title: 'Copias de seguridad', desc: 'Backups diarios encriptados en ubicaciones separadas.' }
              ].map((item, idx) => (
                <div key={idx} className="bg-slate-50 border border-slate-200 rounded-lg p-5 flex items-start gap-4">
                  <div className="w-10 h-10 bg-white border border-slate-200 rounded-lg flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-5 h-5 text-slate-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">{item.title}</h3>
                    <p className="text-sm text-slate-600">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-blue-50/50 border border-blue-100 rounded-lg p-5 mt-6 pl-11">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-slate-700 leading-relaxed">
                  <strong>Transparencia:</strong> Aunque hacemos todo lo posible, ningún sistema es 100% 
                  invulnerable. Si alguna vez hay una brecha de seguridad, te avisaremos inmediatamente 
                  y te guiaremos sobre qué hacer.
                </p>
              </div>
            </div>
          </section>

          {/* SECCIÓN 6 */}
          <section className="border-b border-slate-200 pb-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
                <UserCheck className="w-4 h-4 text-blue-600" />
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-slate-900">6. Tus derechos</h2>
            </div>
            
            <p className="text-slate-600 mb-6 pl-11">
              Tienes control total sobre tus datos. Puedes:
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pl-11">
              {[
                { right: 'Acceder a tus datos', desc: 'Solicitar una copia de toda tu información.' },
                { right: 'Corregir errores', desc: 'Actualizar datos inexactos o incompletos.' },
                { right: 'Eliminar tu cuenta', desc: 'Borrar todos tus datos (derecho al olvido).' },
                { right: 'Retirar consentimiento', desc: 'Cambiar de opinión sobre el uso de tus datos en cualquier momento.' }
              ].map((item, idx) => (
                <div key={idx} className="bg-slate-50 border border-slate-200 rounded-lg p-5 flex items-start gap-4">
                  <div className="w-10 h-10 bg-white border border-slate-200 rounded-lg flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">{item.right}</h3>
                    <p className="text-sm text-slate-600">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-blue-50/50 border border-blue-100 rounded-xl p-6 md:p-8 mt-6 pl-11">
              <h3 className="font-semibold text-slate-900 mb-3">¿Cómo ejercer tus derechos?</h3>
              <p className="text-slate-600 mb-4">
                Simplemente escríbenos a:
              </p>
              <a href="mailto:privacidad@visioner7.pe" className="inline-flex items-center gap-2 bg-white px-5 py-2.5 rounded-lg border border-slate-200 text-slate-700 font-medium hover:border-blue-300 hover:text-blue-600 transition-colors">
                <Mail className="w-4 h-4" />
                privacidad@visioner7.pe
              </a>
              <p className="text-sm text-slate-500 mt-4">
                Te responderemos en un plazo máximo de 30 días.
              </p>
            </div>
          </section>

          {/* SECCIÓN 7 */}
          <section>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
                <Database className="w-4 h-4 text-blue-600" />
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-slate-900">7. ¿Cuánto tiempo guardamos tus datos?</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 pl-11">
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-5">
                <p className="font-semibold text-slate-900 mb-2">Cuenta activa</p>
                <p className="text-sm text-slate-600">Mientras la uses</p>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-5">
                <p className="font-semibold text-slate-900 mb-2">Documentos</p>
                <p className="text-sm text-slate-600">72 horas (por defecto)</p>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-5">
                <p className="font-semibold text-slate-900 mb-2">Registros de auditoría</p>
                <p className="text-sm text-slate-600">7 años (por ley)</p>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-5">
                <p className="font-semibold text-slate-900 mb-2">Datos de facturación</p>
                <p className="text-sm text-slate-600">7 años (impuestos)</p>
              </div>
            </div>
            <p className="text-sm text-slate-600 mt-4 pl-11">
              Después de esos períodos, eliminamos o anonimizamos tus datos de forma segura e irreversible.
            </p>
          </section>
        </div>

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-slate-200 text-center">
          <p className="text-slate-600">
            Tu privacidad es importante para nosotros. Gracias por confiar en Inkless.
          </p>
          <p className="text-sm text-slate-500 mt-2">
            El equipo de Inkless
          </p>
        </div>
      </div>
    </div>
  );
}